import React, {useCallback, useEffect, useState} from 'react';
import {View, FlatList, StyleSheet, RefreshControl, TouchableOpacity, Image} from 'react-native';
import {Text, Chip, FAB, ActivityIndicator, Appbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useOrderStore} from '../../store/useOrderStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import {colors, spacing} from '../../theme';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return {day: String(d.getDate()), month: MONTHS[d.getMonth()]};
};

const FILTER_OPTIONS = [
  {key: 'ALL', statuses: null},
  {key: 'NEW', statuses: ['NEW']},
  {key: 'IN_PROGRESS', statuses: ['IN_PROCESS']},
  {key: 'READY', statuses: ['COMPLETED', 'QUALITY_CHECK', 'READY_FOR_TRIAL']},
  {key: 'DELIVERED', statuses: ['DELIVERED']},
];

const OrderListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {orders, isLoading, pagination, fetchOrders, setFilters, clearFilters} = useOrderStore();
  const {selectedOrgUnitId} = useOrgUnitStore();
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const option = FILTER_OPTIONS.find(f => f.key === activeFilter);
    fetchOrders({page: 0, size: 20, ...(option.statuses ? {statuses: option.statuses} : {}), orgUnitId: selectedOrgUnitId});
  }, [fetchOrders, selectedOrgUnitId]);

  const handleFilter = useCallback(filterKey => {
    setActiveFilter(filterKey);
    const option = FILTER_OPTIONS.find(f => f.key === filterKey);
    if (option.statuses) {
      setFilters({statuses: option.statuses});
      fetchOrders({page: 0, size: 20, statuses: option.statuses, orgUnitId: selectedOrgUnitId});
    } else {
      clearFilters();
      fetchOrders({page: 0, size: 20, orgUnitId: selectedOrgUnitId});
    }
  }, [setFilters, clearFilters, fetchOrders, selectedOrgUnitId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const option = FILTER_OPTIONS.find(f => f.key === activeFilter);
      await fetchOrders({page: 0, size: 20, ...(option.statuses ? {statuses: option.statuses} : {}), orgUnitId: selectedOrgUnitId});
    } finally {
      setRefreshing(false);
    }
  }, [activeFilter, fetchOrders, selectedOrgUnitId]);

  const handleLoadMore = useCallback(() => {
    if (isLoading) return;
    const nextPage = pagination.page + 1;
    if (nextPage < pagination.totalPages) {
      fetchOrders({page: nextPage, size: pagination.size});
    }
  }, [isLoading, pagination, fetchOrders]);

  const getAmountColor = (paymentStatus) => {
    if (paymentStatus === 'PAID') return colors.success;
    if (paymentStatus === 'UNPAID') return colors.error;
    if (paymentStatus === 'PARTIALLY_PAID') return colors.warning;
    return colors.text;
  };

  const renderOrder = useCallback(({item}) => {
    const date = parseDate(item.deliveryDate);
    const amountColor = getAmountColor(item.paymentStatus);
    const summary = item.itemSummary || [];
    const totalItems = summary.reduce((sum, s) => sum + s.quantity, 0);
    const statusColor = colors.status[item.status] || colors.info;
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.card}
        onPress={() => navigation.navigate('OrderView', {orderId: item.id})}>
        <View style={[styles.cardAccent, {backgroundColor: statusColor}]} />
        <View style={styles.cardBody}>
          {/* Top row: order number + amount */}
          <View style={styles.topRow}>
            <View style={styles.orderIdRow}>
              <Text style={styles.orderNumber}>{item.orderNumber}</Text>
              {item.urgent && <Icon name="flag" size={14} color={colors.error} style={{marginLeft: 4}} />}
            </View>
            <View style={styles.amountCol}>
              {item.paymentStatus === 'PARTIALLY_PAID' && (
                <Text style={styles.totalAmountStrike}>
                  {'\u20B9'}{Number((item.totalAmount || 0) - (item.discountAmount || 0) + (item.taxAmount || 0)).toLocaleString()}
                </Text>
              )}
              <Text style={[styles.orderAmount, {color: amountColor}]}>
                {'\u20B9'}{Number(
                  item.paymentStatus === 'PAID'
                    ? (item.totalAmount || 0) - (item.discountAmount || 0) + (item.taxAmount || 0)
                    : (item.balanceAmount || 0)
                ).toLocaleString()}
              </Text>
            </View>
          </View>
          {/* Customer name */}
          <Text style={styles.customerName} numberOfLines={1}>{item.customerName}</Text>
          {/* Items summary as text pills */}
          {summary.length > 0 && (
            <View style={styles.summaryRow}>
              {summary.map((s, idx) => (
                <View key={idx} style={styles.itemPill}>
                  {s.catalogItemIcon ? (
                    <Image source={{uri: s.catalogItemIcon}} style={styles.pillIcon} />
                  ) : null}
                  <Text style={styles.pillText}>
                    {s.catalogItemName || '?'}{s.quantity > 1 ? ` \u00D7${s.quantity}` : ''}
                  </Text>
                </View>
              ))}
            </View>
          )}
          {/* Bottom row: status + delivery date */}
          <View style={styles.bottomRow}>
            <View style={[styles.statusBadge, {backgroundColor: statusColor + '18'}]}>
              <View style={[styles.statusDot, {backgroundColor: statusColor}]} />
              <Text style={[styles.statusText, {color: statusColor}]}>{t(`order.status.${item.status}`)}</Text>
            </View>
            {date && (
              <View style={styles.dateTag}>
                <Icon name="calendar-outline" size={13} color={colors.textSecondary} />
                <Text style={styles.dateText}>{date.day} {date.month}</Text>
              </View>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [navigation, t]);

  const filterLabels = {ALL: 'All', NEW: 'New', IN_PROGRESS: 'In Progress', READY: 'Ready', DELIVERED: 'Delivered'};

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action icon="menu" onPress={() => navigation.dispatch(DrawerActions.openDrawer())} />
        <Appbar.Content title={t('order.title')} />
        <Appbar.Action icon="filter-variant" onPress={() => navigation.navigate('OrderSearchFilters')} />
        <OrgUnitSelector />
      </Appbar.Header>

      <View style={styles.filterRow}>
        {FILTER_OPTIONS.map(opt => (
          <Chip
            key={opt.key}
            selected={activeFilter === opt.key}
            showSelectedCheck={activeFilter === opt.key}
            selectedColor={activeFilter === opt.key ? colors.white : undefined}
            onPress={() => handleFilter(opt.key)}
            style={[styles.filterChip, activeFilter === opt.key && styles.filterChipActive]}
            textStyle={activeFilter === opt.key ? styles.filterChipTextActive : styles.filterChipText}>
            <Text style={{fontSize: 13, color: activeFilter === opt.key ? '#fff' : '#000', marginTop: 3}}>{filterLabels[opt.key]}</Text>
          </Chip>
        ))}
      </View>

      {isLoading && orders.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={orders}
          renderItem={renderOrder}
          keyExtractor={item => String(item.id)}
          contentContainerStyle={styles.listContent}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.3}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text variant="titleMedium" style={styles.emptyTitle}>{t('common.noData')}</Text>
            </View>
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
          }
        />
      )}

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={() => navigation.navigate('OrderCreate')}
        color={colors.white}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  filterRow: {
    flexDirection: 'row', paddingHorizontal: spacing.sm, paddingVertical: spacing.sm,
    backgroundColor: colors.surface, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.background},
  filterChipActive: {backgroundColor: colors.primary},
  filterChipText: {color: colors.text, fontSize: 12},
  filterChipTextActive: {color: colors.white, fontSize: 12},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.sm, paddingBottom: 80, gap: spacing.sm},
  card: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 10,
    overflow: 'hidden',
    // shadow
    shadowColor: '#000', shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06, shadowRadius: 4, elevation: 2,
  },
  cardAccent: {width: 4},
  cardBody: {flex: 1, padding: spacing.md - 2},
  topRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'},
  orderIdRow: {flexDirection: 'row', alignItems: 'center'},
  orderNumber: {fontWeight: '700', color: colors.primary, fontSize: 14},
  customerName: {color: colors.text, fontSize: 13, marginTop: 3},
  summaryRow: {flexDirection: 'row', flexWrap: 'wrap', marginTop: 8, gap: 6},
  itemPill: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#F5F5F5', borderRadius: 6,
    paddingHorizontal: 8, paddingVertical: 4,
  },
  pillIcon: {width: 16, height: 16, borderRadius: 3, marginRight: 5},
  pillText: {fontSize: 12, color: colors.text, fontWeight: '500'},
  bottomRow: {flexDirection: 'row', alignItems: 'center', marginTop: 10, gap: 10},
  statusBadge: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6,
  },
  statusDot: {width: 6, height: 6, borderRadius: 3, marginRight: 5},
  statusText: {fontSize: 12, fontWeight: '600'},
  dateTag: {flexDirection: 'row', alignItems: 'center', gap: 4},
  dateText: {fontSize: 12, color: colors.textSecondary, fontWeight: '500'},
  amountCol: {alignItems: 'flex-end'},
  totalAmountStrike: {fontSize: 11, color: colors.textSecondary, textDecorationLine: 'line-through', marginBottom: 1},
  orderAmount: {fontSize: 15, fontWeight: '700'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 2},
  emptyTitle: {color: colors.textSecondary},
  fab: {position: 'absolute', right: spacing.md, bottom: spacing.md, backgroundColor: colors.primary},
});

export default OrderListScreen;
